import { Button, ButtonLink } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SubscriptionIdentification, SubscriptionLookup } from '@dash-lite/sdk';
import { CalendarDays, CheckCircle2, CreditCard, Mail } from 'lucide-react';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { Page, PageHeader } from '@/components/layout/page';
import { EntitlementStatusBadge } from '@/components/payment/entitlement-summary';
import { DevNote } from '@/components/ui/dev-note';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardBody, CardHeader, Container, DataList, DataRow, Section } from '@/components/ui/primitives';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, isApiError, presentError } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Résiliation sans connexion — `POST /subscription/lookup` puis
 * `POST /subscription/cancel`.
 *
 * Pourquoi ne pas simplement demander de se connecter ? Parce qu'un compte né
 * d'un **paiement invité** n'a pas de mot de passe choisi par son titulaire :
 * exiger une connexion pour résilier rendrait la résiliation difficile,
 * exactement le contraire de ce que demande la conformité « résiliation
 * facile ».
 *
 * Le client se fait donc reconnaître avec ce qu'il a sous les yeux au moment
 * où il veut agir : **son relevé bancaire**. Deux voies, toutes deux fondées
 * sur les 4 derniers chiffres de la carte :
 *
 * | Le client se souvient de… | Il fournit                |
 * | ------------------------- | ------------------------- |
 * | son adresse email         | `email` + `card_last4`    |
 * | la date du prélèvement    | `payment_date` + `card_last4` |
 *
 * La date tolère un jour d'écart de part et d'autre : celle qu'affiche la
 * banque n'est pas toujours celle du débit réel.
 *
 * Le parcours se fait en deux temps — on retrouve, on montre, puis seulement
 * on résilie. `lookup` ne modifie rien : c'est ce qui permet d'afficher un
 * écran de confirmation honnête.
 */

type Method = 'email' | 'payment_date';

export default function CancelSubscriptionPage() {
    const [method, setMethod] = useState<Method>('email');
    const [email, setEmail] = useState('');
    const [paymentDate, setPaymentDate] = useState('');
    const [cardLast4, setCardLast4] = useState('');

    const [found, setFound] = useState<SubscriptionLookup | null>(null);
    const [canceled, setCanceled] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const fields = fieldErrors(error);

    /**
     * Construit l'identification attendue par l'API. Le type
     * `SubscriptionIdentification` du SDK impose déjà, à la compilation, de
     * fournir `email` **ou** `payment_date`, jamais les deux.
     */
    function buildIdentification(): SubscriptionIdentification {
        return method === 'email'
            ? { email: email.trim(), card_last4: cardLast4.trim() }
            : { payment_date: paymentDate, card_last4: cardLast4.trim() };
    }

    async function handleLookup(event: FormEvent): Promise<void> {
        event.preventDefault();
        setSubmitting(true);
        setError(null);
        setFound(null);

        try {
            setFound(await dashlite.lookupSubscription(buildIdentification()));
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    async function handleCancel(): Promise<void> {
        setSubmitting(true);
        setError(null);

        try {
            const result = await dashlite.cancelSubscription(buildIdentification());
            setFound(result);
            setCanceled(true);
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Page title="Résilier mon abonnement">
            <PageHeader
                eyebrow="Résiliation"
                title="Résilier mon abonnement"
                description="Sans connexion et sans justification. Les informations demandées sont celles qui figurent sur votre relevé bancaire."
            />

            <Section>
                <Container className="grid items-start gap-10 lg:grid-cols-[1.1fr_1fr]">
                    <div className="space-y-6">
                        {canceled && found ? (
                            <CancellationConfirmed lookup={found} />
                        ) : found ? (
                            <SubscriptionFound
                                lookup={found}
                                submitting={submitting}
                                onCancel={handleCancel}
                                onBack={() => {
                                    setFound(null);
                                    setError(null);
                                }}
                            />
                        ) : (
                            <Card>
                                <CardHeader
                                    title="Retrouver votre abonnement"
                                    description="Aucune modification n'est faite à cette étape."
                                />
                                <CardBody>
                                    <form onSubmit={handleLookup} className="space-y-5" noValidate>
                                        <MethodSwitch method={method} onChange={setMethod} />

                                        {method === 'email' ? (
                                            <Input
                                                data-dashlite="cancel-email"
                                                label="Adresse email"
                                                type="email"
                                                required
                                                autoComplete="email"
                                                value={email}
                                                error={fields.email}
                                                onChange={(event) => setEmail(event.target.value)}
                                                hint="Celle utilisée lors de votre achat."
                                            />
                                        ) : (
                                            <Input
                                                label="Date du prélèvement"
                                                type="date"
                                                required
                                                value={paymentDate}
                                                error={fields.payment_date}
                                                onChange={(event) => setPaymentDate(event.target.value)}
                                                hint="Telle qu'elle apparaît sur votre relevé. Un jour d'écart est toléré."
                                            />
                                        )}

                                        <Input
                                            data-dashlite="cancel-card-last4"
                                            label="4 derniers chiffres de la carte"
                                            inputMode="numeric"
                                            pattern="[0-9]{4}"
                                            maxLength={4}
                                            required
                                            placeholder="4242"
                                            value={cardLast4}
                                            error={fields.card_last4}
                                            onChange={(event) =>
                                                setCardLast4(event.target.value.replace(/\D/g, '').slice(0, 4))
                                            }
                                        />

                                        {error !== null && Object.keys(fields).length === 0 && (
                                            <LookupError error={error} onSwitchMethod={() => setMethod(method === 'email' ? 'payment_date' : 'email')} />
                                        )}

                                        <Button
                                            data-dashlite="cancel-submit"
                                            type="submit"
                                            size="lg"
                                            className="w-full"
                                            loading={submitting}
                                        >
                                            Retrouver mon abonnement
                                        </Button>
                                    </form>
                                </CardBody>
                            </Card>
                        )}
                    </div>

                    <div className="space-y-6 lg:sticky lg:top-28">
                        <Alert tone="info" title="Vous avez un mot de passe ?">
                            Vous pouvez aussi résilier depuis votre compte, via le portail de facturation Stripe.
                            <ButtonLink to={paths.account} variant="secondary" size="sm" className="mt-3">
                                Aller à mon compte
                            </ButtonLink>
                        </Alert>

                        <DevNote
                            title="Ce que fait cet écran"
                            endpoints={['POST /subscription/lookup', 'POST /subscription/cancel']}
                        >
                            <p>
                                <code className="font-mono">lookup</code> ne modifie rien : il sert uniquement à
                                afficher un écran de confirmation. <code className="font-mono">cancel</code> prend
                                exactement les mêmes paramètres.
                            </p>
                            <p>
                                L'email revient <strong>masqué</strong> (<code className="font-mono">c*****@exemple.fr</code>) :
                                il confirme au client qu'il s'agit bien de son compte, sans révéler une adresse à qui
                                aurait deviné des chiffres de carte.
                            </p>
                            <p>
                                Un compte inconnu et de mauvais chiffres renvoient{' '}
                                <strong>le même</strong> code <code className="font-mono">subscription_not_found</code>.
                                C'est volontaire — n'affichez jamais de message qui distinguerait les deux cas.
                            </p>
                            <p>
                                Appeler <code className="font-mono">cancel</code> deux fois est sans effet
                                supplémentaire : le client qui revient vérifier retrouve son état, sans message
                                d'erreur.
                            </p>
                        </DevNote>
                    </div>
                </Container>
            </Section>
        </Page>
    );
}

function MethodSwitch({ method, onChange }: { method: Method; onChange: (method: Method) => void }) {
    const options: Array<{ value: Method; label: string; icon: typeof Mail }> = [
        { value: 'email', label: 'Avec mon email', icon: Mail },
        { value: 'payment_date', label: 'Avec la date du prélèvement', icon: CalendarDays },
    ];

    return (
        <div role="radiogroup" aria-label="Méthode d'identification" className="grid gap-2 sm:grid-cols-2">
            {options.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    role="radio"
                    aria-checked={method === option.value}
                    onClick={() => onChange(option.value)}
                    className={cn(
                        'flex items-center gap-2.5 rounded-lg border px-3.5 py-3 text-left text-sm transition-colors',
                        method === option.value
                            ? 'border-brand-500 bg-brand-50 text-brand-800'
                            : 'border-line text-ink-soft hover:border-line-strong',
                    )}
                >
                    <option.icon className="size-4 shrink-0" aria-hidden />
                    {option.label}
                </button>
            ))}
        </div>
    );
}

/** Récapitulatif avant la résiliation — dernière étape réversible. */
function SubscriptionFound({
    lookup,
    submitting,
    onCancel,
    onBack,
}: {
    lookup: SubscriptionLookup;
    submitting: boolean;
    onCancel: () => void;
    onBack: () => void;
}) {
    return (
        <Card>
            <CardHeader
                title="Abonnement trouvé"
                description="Vérifiez qu'il s'agit bien du vôtre avant de confirmer."
                action={<EntitlementStatusBadge entitlement={lookup.entitlement} />}
            />

            <CardBody className="space-y-5">
                <DataList>
                    <DataRow label="Compte" value={<span className="font-mono">{lookup.email}</span>} />
                    <DataRow label="Offre" value={lookup.offer ?? '—'} />
                    <DataRow
                        label="Carte"
                        value={
                            <span className="inline-flex items-center gap-2">
                                <CreditCard className="text-ink-muted size-4" aria-hidden />
                                <span className="capitalize">{lookup.card.brand ?? 'Carte'}</span>
                                <span className="font-mono">•••• {lookup.card.last4 ?? '????'}</span>
                            </span>
                        }
                    />
                    <DataRow label="Accès ouvert depuis" value={formatDateTime(lookup.entitlement.starts_at)} />
                </DataList>

                <Alert tone="caution" title="Ce qui se passera">
                    L'arrêt prend effet à la fin de la période déjà réglée : votre accès reste ouvert jusque-là, et
                    plus aucun prélèvement n'aura lieu. Une confirmation vous sera envoyée par email.
                </Alert>

                <div className="flex flex-wrap gap-3">
                    <Button data-dashlite="cancel-confirm" variant="danger" loading={submitting} onClick={onCancel}>
                        Confirmer la résiliation
                    </Button>
                    <Button variant="ghost" onClick={onBack} disabled={submitting}>
                        Ce n'est pas mon abonnement
                    </Button>
                </div>
            </CardBody>
        </Card>
    );
}

function CancellationConfirmed({ lookup }: { lookup: SubscriptionLookup }) {
    return (
        <Card>
            <CardBody className="space-y-5 py-10 text-center">
                <span className="bg-positive-soft text-positive mx-auto flex size-14 items-center justify-center rounded-full">
                    <CheckCircle2 className="size-7" aria-hidden />
                </span>

                <div>
                    <h2 className="font-display text-2xl tracking-tight">Votre abonnement est résilié</h2>
                    <p className="text-ink-soft mx-auto mt-3 max-w-md text-sm leading-relaxed">
                        Plus aucun prélèvement n'aura lieu. Votre accès reste ouvert jusqu'au{' '}
                        <strong>{formatDateTime(lookup.entitlement.ends_at)}</strong> — vous conservez ce que vous avez
                        déjà réglé. Une confirmation part par email à <span className="font-mono">{lookup.email}</span>.
                    </p>
                </div>

                <div className="flex justify-center">
                    <ButtonLink to={paths.home} variant="secondary">
                        Retour à l'accueil
                    </ButtonLink>
                </div>
            </CardBody>
        </Card>
    );
}

/** Message d'échec d'identification, avec la bonne suggestion selon le code. */
function LookupError({ error, onSwitchMethod }: { error: unknown; onSwitchMethod: () => void }) {
    const presentation = presentError(error);

    return (
        <Alert tone="critical" title={presentation.message}>
            <p>{presentation.hint}</p>

            {isApiError(error, 'subscription_not_found') && (
                <p className="mt-2">
                    <button type="button" onClick={onSwitchMethod} className="underline">
                        Essayer l'autre méthode d'identification
                    </button>
                </p>
            )}
        </Alert>
    );
}
